import { useEffect, useState, type ReactNode } from "react";
import { CalendarClock, CircleCheck, ClipboardCheck, Database, FileSpreadsheet, Gauge, GitCompareArrows, Inbox, OctagonAlert, TriangleAlert } from "lucide-react";
import { uploadLabel } from "../../shared/upload";
import type { DashboardResponse, IntegrityResponse, IpaDetailResponse, IpaLineItem, IpaListResponse, RunResponse, Severity, SourcesResponse } from "../../shared/api";
import { BOX_NAMES, SOURCE_TYPE_LABEL } from "../../shared/sources";
import { severityLabel } from "../../shared/findings";
import { formatAuditTime } from "../../shared/audit";
import { daysLeftText, formatDate, formatKg, formatNumber, formatPct } from "../../shared/versions";
import { BarList, CumulativeLine, Meter, RingMeter, SegmentBar } from "@/components/charts";
import { EmptyState } from "@/components/EmptyState";
import { ErrorAlert } from "@/components/ErrorAlert";
import { StatCardSkeleton, TableSkeleton } from "@/components/Loading";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { SeverityBadge, StatusBadge } from "@/components/StatusBadge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { BOX_ICON, FooterLine, boxBadge, boxTone } from "@/components/sources-shared";
import { BOX_LABEL, formatDay } from "@/lib/upload";
import { Card, CardAction, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserAvatar } from "@/components/UserAvatar";
import { errorText, getDashboard, getIntegrity, getIpaDetail, getIpaList, getLatestRun, getSources } from "@/lib/client";
import { navigate } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { pathOf, screens } from "../screens";

/** Bir isteğin durumu: veri, hata ya da (ikisi de yoksa) yükleniyor. Her bölüm kendi durumunu taşır, istekler birbirini beklemez. */
export type Part<T> = { data?: T; error?: string };

const NO_RUN = "Henüz mutabakat çalıştırılmadı.";
const NO_DECLARATION = "Beyan kalemi yok.";
const SEVERITY_ORDER: { v: Severity; label: string; color: "critical" | "warning" | "info" }[] = (["critical", "warning", "info"] as const).map((v) => ({ v, label: severityLabel[v], color: v }));
const MAX_FINDINGS = 5; // yalnız gösterim kesmesi (ui.md §12.11)

/** KPI 3'ün bulduğu belge: en yakın süre sonlu DİİB (KPI ve DİİB kartı aynı belgeyi gösterir). */
const nearestDoc = (d: DashboardResponse, ipa: Part<IpaListResponse["items"]>) => d.ipaDaysLeft === null ? undefined : ipa.data?.find((i) => i.daysLeft === d.ipaDaysLeft);

/** Client-side link to a screen (no page reload). */
function ScreenLink({ href, className = "rounded-sm text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring", children }: { href: string; className?: string; children: ReactNode }) {
  return <a href={href} className={className} onClick={(e) => navigate(e, href)}>{children}</a>;
}

const ipaHref = (ipaNo: string) => `${pathOf("E5")}?ipa=${encodeURIComponent(ipaNo)}`;
const link = (id: string, text: string) => ({ href: pathOf(id), text });

function CardHead({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <CardHeader className="border-b px-4 py-3 [.border-b]:pb-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {action && <CardAction>{action}</CardAction>}
    </CardHeader>
  );
}

function Kpis({ d, ipa }: { d: DashboardResponse; ipa: Part<IpaListResponse["items"]> }) {
  const c = d.openFindingCount, s = d.sourceStatus, noRun = d.declaredKg === null; // declaredKg yalnız koşudan önce null: hesaplanmamış sıfır yazılmaz
  const doc = nearestDoc(d, ipa);
  return (
    <ul aria-label="Durum kartları" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <li><StatCard title="Açık uyumsuzluk" icon={OctagonAlert} value={noRun ? null : formatNumber(d.openFindingTotal)} empty={NO_RUN} description="açık bulgu" link={link("E3", "Mutabakat ekranına git")}>
        {!noRun && <SegmentBar segments={SEVERITY_ORDER.map((x) => ({ label: x.label, count: c[x.v], color: x.color, href: `${pathOf("E3")}?severity=${x.v}` }))} />}
      </StatCard></li>
      <li><StatCard title="İzlenebilirlik oranı" icon={Gauge}
        value={d.traceabilityPct === null ? null : formatPct(d.traceabilityPct)}
        empty={noRun ? NO_RUN : NO_DECLARATION}
        visual={<RingMeter pct={d.traceabilityPct} />}
        description={d.matchedKg !== null && d.declaredKg !== null ? `${formatKg(d.matchedKg)} / ${formatKg(d.declaredKg)} kg ERP girişiyle eşleşti` : undefined}
        link={link("E4", "İzleme zincirine git")} /></li>
      <li><StatCard title="DİİB süre sonu" icon={CalendarClock}
        value={d.ipaDaysLeft === null ? null : daysLeftText(d.ipaDaysLeft)}
        empty="DİİB süre sonu bilgisi yok."
        description={d.ipaDaysLeft !== null && d.ipaDaysLeft >= 0 ? "en yakın DİİB süre sonuna" : undefined}
        link={doc ? { href: ipaHref(doc.ipaNo), text: "DİİB paneline git" } : link("E5", "DİİB paneline git")}>
        {ipa.error ? <ErrorAlert>{ipa.error}</ErrorAlert> : doc && <p className="text-sm text-muted-foreground">{doc.ipaNo} · {formatDate(doc.endDate)}</p>}
      </StatCard></li>
      <li><StatCard title="Kaynak durumu" icon={Database}
        value={`${formatNumber(s.loadedSourceTypes)} / ${formatNumber(s.totalSourceTypes)}`}
        description="kaynak tipi yüklü"
        link={link("E2", "Veri kaynaklarına git")}>
        <SegmentBar legend={false} segments={Array.from({ length: s.totalSourceTypes }, (_, i) => ({ label: `Kaynak tipi ${i + 1}`, count: 1, color: i < s.loadedSourceTypes ? "primary" : "track" }))} />
        {s.loadedSourceTypes === 0 ? <p className="text-sm">Henüz dosya yüklenmedi.</p>
          : s.warnedSourceTypes.length === 0 ? <p className="text-sm">Uyarılı kaynak yok</p>
          : <p className="flex items-center gap-1.5 text-sm font-medium text-warning-foreground"><TriangleAlert aria-hidden="true" className="size-4" />Uyarılı kaynak: {formatNumber(s.warnedSourceTypes.length)}</p>}
      </StatCard></li>
    </ul>
  );
}

function OpenFindings({ d }: { d: DashboardResponse }) {
  return (
    <Card className="gap-0 overflow-clip p-0">
      <CardHead title="Açık bulgular" action={<ScreenLink href={pathOf("E3")} className="rounded-sm text-sm text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring">Tümünü gör ({formatNumber(d.openFindingTotal)}) →</ScreenLink>} />
      {d.openFindings.length === 0 ? (
        d.declaredKg === null
          ? <EmptyState icon={GitCompareArrows} title="Henüz mutabakat çalıştırılmadı">Mutabakat ekranından çalıştırın.</EmptyState>
          : <EmptyState icon={CircleCheck} title="Açık bulgu yok">Son mutabakatta açık bulgu kalmadı.</EmptyState>
      ) : (
        <Table>
          <TableHeader><TableRow className="bg-muted/60"><TableHead className="px-4 text-xs font-medium text-muted-foreground">Şiddet</TableHead><TableHead className="text-xs font-medium text-muted-foreground">Bulgu</TableHead><TableHead className="px-4 text-xs font-medium text-muted-foreground">Açılış</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.openFindings.slice(0, MAX_FINDINGS).map((f) => (
              <TableRow key={f.findingId}>
                <TableCell className="px-4"><SeverityBadge severity={f.severity} /></TableCell>
                <TableCell className="whitespace-normal"><ScreenLink href={`${pathOf("E3")}?finding=${f.findingId}`}>{f.title}</ScreenLink></TableCell>
                <TableCell className="px-4 whitespace-nowrap"><span className="sm:hidden">{formatDay(f.openedAt)}</span><span className="hidden sm:inline">{formatAuditTime(f.openedAt)}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function LatestUploads({ d }: { d: DashboardResponse }) {
  return (
    <Card className="gap-0 overflow-clip p-0">
      <CardHead title="Son yüklemeler" action={<ScreenLink href={pathOf("E2")} className={GO}>Tümünü gör →</ScreenLink>} />
      {d.latestUploads.length === 0 ? <EmptyState icon={Inbox} title="Henüz dosya yüklenmedi">Veri Kaynakları ekranından yükleyin.</EmptyState> : (
        <ul className="divide-y">
          {d.latestUploads.map((u) => (
            <li key={u.uploadId} className="flex items-center gap-3 px-4 py-3">
              <FileSpreadsheet aria-hidden="true" className="size-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <ScreenLink href={`${pathOf("E2")}/uploads/${u.uploadId}`} className="block truncate rounded-sm text-sm font-medium text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring">{uploadLabel({ id: u.uploadId, ...u })}</ScreenLink>
                <p className="flex flex-wrap gap-x-1.5 text-xs text-muted-foreground"><span>{SOURCE_TYPE_LABEL[u.sourceType]}</span><span aria-hidden="true">·</span><span>{formatAuditTime(u.createdAt)}</span><span aria-hidden="true">·</span><span>{formatNumber(u.rowCount)} kayıt</span></p>
                <p className="mt-1 flex min-w-0 items-center gap-2"><UserAvatar name={u.uploadedBy} /><span className="truncate text-xs text-muted-foreground">{u.uploadedBy}</span></p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const GO = "rounded-sm text-sm text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring";

/** Bütünlük şeridi (ui.md §12.11): E2'nin alt satırı + dosya sayıları. Sayılar GET /api/integrity ve /api/sources'tan. */
function IntegrityStrip({ sources, integrity }: { sources: Part<SourcesResponse>; integrity: Part<IntegrityResponse> }) {
  const err = sources.error ?? integrity.error;
  if (err) return <ErrorAlert>{err}</ErrorAlert>;
  if (!sources.data || !integrity.data) return <div aria-busy="true"><Skeleton className="h-12 w-full" /></div>;
  const i = integrity.data;
  const empty = Object.values(sources.data.boxes).every((b) => b.files.length === 0);
  return (
    <FooterLine total={sources.data.totalRecords} integrity={i} empty={empty} className="flex-row flex-wrap items-center gap-x-4 gap-y-2">
      <span className="text-sm text-muted-foreground">{formatNumber(i.fileCount)} dosya</span>
      {i.manualEntryCount > 0 && <span className="text-sm text-muted-foreground">Elle giriş {formatNumber(i.manualEntryCount)}</span>}
      <span className="text-sm text-muted-foreground">Kayıtlı değişiklik {formatNumber(i.recordedChangeCount)}</span>
      <ScreenLink href={pathOf("E6")} className={cn(GO, "ml-auto")}>Veri Geçmişine git →</ScreenLink>
    </FooterLine>
  );
}

/** Beyanname ve ERP eşleşmesi (ui.md §12.11). Koşu özeti `GET /api/runs/latest`'ten; süzme gösterimdir, sayı üretmez. */
function MatchCard({ run }: { run: Part<RunResponse | null> }) {
  const head = <CardHead title="Beyanname ve ERP eşleşmesi" action={<ScreenLink href={pathOf("E3")} className={GO}>Mutabakata git →</ScreenLink>} />;
  if (run.error) return <Card className="gap-0 overflow-clip p-0">{head}<CardContent className="p-4"><ErrorAlert>{run.error}</ErrorAlert></CardContent></Card>;
  if (run.data === undefined) return <Card className="gap-0 overflow-clip p-0">{head}<CardContent className="p-4"><Pending rows /></CardContent></Card>;
  if (run.data === null) return <Card className="gap-0 overflow-clip p-0">{head}<EmptyState icon={GitCompareArrows} title="Henüz mutabakat çalıştırılmadı">Mutabakat ekranından çalıştırın.</EmptyState></Card>;
  const r = run.data, sm = r.summary;
  const items = sm.items.filter((x) => x.rule === "R1" && x.erpKg !== null).slice(0, MAX_FINDINGS);
  return (
    <Card className="gap-0 overflow-clip p-0">
      {head}
      <CardContent className="flex flex-col gap-3 px-4 py-4">
        <SegmentBar segments={[{ label: "Eşleşen", count: sm.matchedCount, color: "primary" }, { label: "Eşleşmeyen", count: sm.unmatchedCount, color: "critical" }]} />
        <p className="text-2xl font-semibold tabular-nums">Toplam fark {formatKg(sm.totalDiffKg)} kg</p>
        <ul className="divide-y text-sm">
          {items.map((x) => (
            <li key={`${x.declaration}/${x.itemNo}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <span className="font-mono text-sm">{x.declaration}</span>
              {x.matched ? <StatusBadge tone="success">Eşleşti</StatusBadge> : <StatusBadge tone="critical">Fark {x.diffKg === null ? "" : `${formatKg(x.diffKg)} kg`}</StatusBadge>}
              <ScreenLink href={`${pathOf("E4")}?ref=${encodeURIComponent(x.declaration)}`} className={cn(GO, "ml-auto")}>İzle →</ScreenLink>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">Bu koşuda {formatNumber(r.openedCount)} bulgu açıldı, {formatNumber(r.closedCount)} bulgu kapandı</p>
      </CardContent>
    </Card>
  );
}

const LINE_TITLE = { imports: "İthalat", exports: "İhracat" } as const;
function CommitRow({ line }: { line: IpaLineItem }) {
  const t = LINE_TITLE[line.commitmentType];
  const pct = line.fulfilmentPct === null ? "—" : formatPct(line.fulfilmentPct);
  const kg = `${formatKg(line.fulfilledKg)} / ${formatKg(line.commitmentKg)} kg`;
  return (
    <div className="grid grid-cols-[4.5rem_1fr_4rem] sm:grid-cols-[5rem_1fr_4rem_11rem] items-center gap-3 text-sm">
      <span>{t}</span>
      <Meter label={`${t} gerçekleşme oranı`} pct={line.fulfilmentPct} valueText={kg} />
      <span className="text-right tabular-nums">{pct}</span>
      <span className="col-span-3 tabular-nums text-muted-foreground sm:col-span-1 sm:text-right">{kg}</span>
    </div>
  );
}

/** DİİB taahhüt gerçekleşmesi (ui.md §12.11): KPI 3'ün bulduğu belge.  */
function IpaCard({ d, ipa, detail }: { d: DashboardResponse; ipa: Part<IpaListResponse["items"]>; detail: Part<IpaDetailResponse> }) {
  const doc = nearestDoc(d, ipa);
  const empty = ipa.data !== undefined && !doc;
  const head = <CardHead title="DİİB taahhüt gerçekleşmesi" action={<ScreenLink href={doc ? ipaHref(doc.ipaNo) : pathOf("E5")} className={GO}>DİİB paneline git →</ScreenLink>} />;
  const err = ipa.error ?? (doc ? detail.error : undefined);
  const dt = detail.data;
  const forecast = dt?.exports?.forecastText;
  return (
    <Card className="gap-0 overflow-clip p-0">
      {head}
      {empty ? <EmptyState icon={ClipboardCheck} title="DİİB dosyası yüklenmedi">Veri Kaynakları ekranından yükleyin.</EmptyState>
        : err ? <CardContent className="p-4"><ErrorAlert>{err}</ErrorAlert></CardContent>
        : !doc || !dt ? <CardContent className="p-4"><Pending rows /></CardContent>
        : (
          <CardContent className="flex flex-col gap-3 px-4 py-4">
            <p className="text-sm text-muted-foreground">{doc.ipaNo} · Süre sonu {formatDate(doc.endDate)}</p>
            {dt.imports && <CommitRow line={dt.imports} />}
            {dt.exports && <CommitRow line={dt.exports} />}
            {dt.exports && <CumulativeLine label="İhracat birikimli gerçekleşme" months={dt.exports.months} commitmentKg={dt.exports.commitmentKg} />}
            {forecast && (
              <Alert role="note" className="border-warning-border bg-warning text-warning-foreground">
                <TriangleAlert aria-hidden="true" />
                <AlertDescription className="text-warning-foreground"><p data-testid="forecast">{forecast}</p></AlertDescription>
              </Alert>
            )}
          </CardContent>
        )}
    </Card>
  );
}

/** Dört sistem (ui.md §12.11): kutu başına kaynak tipi çubukları, ölçek dört grupta ortak. */
function FourSystems({ s }: { s: SourcesResponse }) {
  const boxes = BOX_NAMES.map((n) => [n, s.boxes[n]] as const);
  const max = Math.max(0, ...boxes.flatMap(([, b]) => b.files.map((f) => f.recordCount)));
  return (
    <Card className="gap-0 overflow-clip p-0">
      <CardHead title="Dört sistem" action={<ScreenLink href={pathOf("E2")} className={GO}>Veri kaynaklarına git →</ScreenLink>} />
      <CardContent className="flex flex-col gap-5 px-4 py-4">
        {boxes.map(([n, b]) => {
          const Icon = BOX_ICON[n], badge = boxBadge(b);
          return (
            <section key={n} aria-labelledby={`sys-${n}`} className="flex min-w-0 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Icon aria-hidden="true" className="size-4 text-primary" />
                <h3 id={`sys-${n}`} className="text-sm font-semibold">{BOX_LABEL[n]}</h3>
                {badge && <StatusBadge tone={boxTone(b)}>{badge}</StatusBadge>}
              </div>
              <BarList max={max} items={b.sourceTypes.map((t) => ({ label: SOURCE_TYPE_LABEL[t] ?? t, value: b.files.find((f) => f.sourceType === t)?.recordCount ?? null }))} />
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
}

const Pending = ({ rows }: { rows?: boolean }) => <div aria-busy="true">{rows ? <TableSkeleton /> : <StatCardSkeleton />}</div>;

function RunMeta({ run }: { run: Part<RunResponse | null> }) {
  if (run.error) return <ErrorAlert>{run.error}</ErrorAlert>;
  if (run.data === undefined) return null;
  if (run.data === null) return <span>{NO_RUN}</span>;
  return (
    <>
      <UserAvatar name={run.data.runBy} />
      <span>Son mutabakat: Koşu #{run.data.runId} · {formatAuditTime(run.data.startedAt)} · {run.data.runBy}</span>
    </>
  );
}

/** Her sayı API'den gelir (GET /api/dashboard, /api/runs/latest, /api/ipa, /api/ipa/detail, /api/sources, /api/integrity); burada hesap yapılmaz. */
export function DashboardView({ d, run, ipa, ipaDetail, sources, integrity }: {
  d: Part<DashboardResponse>; run: Part<RunResponse | null>; ipa: Part<IpaListResponse["items"]>; ipaDetail: Part<IpaDetailResponse>;
  sources: Part<SourcesResponse>; integrity: Part<IntegrityResponse>;
}) {
  const meta = <RunMeta run={run} />;
  const systems = sources.error ? <ErrorAlert>{sources.error}</ErrorAlert> : sources.data ? <FourSystems s={sources.data} /> : <Pending rows />;
  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader title="Durum Panosu" description={screens[0]!.description} meta={run.data === undefined && !run.error ? undefined : meta} />
      {d.error && <ErrorAlert>{d.error}</ErrorAlert>}
      {d.data ? (
        <>
          <Kpis d={d.data} ipa={ipa} />
          <IntegrityStrip sources={sources} integrity={integrity} />
          <div className="grid items-start gap-4 xl:grid-cols-3">
            <div className="min-w-0 xl:col-span-2 xl:row-start-1"><IpaCard d={d.data} ipa={ipa} detail={ipaDetail} /></div>
            <div className="min-w-0 xl:col-start-3 xl:row-start-1"><MatchCard run={run} /></div>
            <div className="min-w-0 xl:col-span-2 xl:row-start-2"><OpenFindings d={d.data} /></div>
            <div className="min-w-0 xl:col-start-3 xl:row-span-2 xl:row-start-2"><LatestUploads d={d.data} /></div>
            <div className="min-w-0 xl:col-span-2 xl:row-start-3">{systems}</div>
          </div>
        </>
      ) : !d.error && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /></div>
          <Pending rows />
        </>
      )}
    </main>
  );
}

function useLoad<T>(fn: () => Promise<T>): Part<T> {
  const [p, setP] = useState<Part<T>>({});
  useEffect(() => { fn().then((data) => setP({ data })).catch((e) => setP({ error: errorText(e) })); }, []);
  return p;
}

export function DashboardScreen() {
  const d = useLoad(getDashboard), run = useLoad(getLatestRun), sources = useLoad(getSources), integrity = useLoad(getIntegrity);
  const list = useLoad(getIpaList);
  const ipa: Part<IpaListResponse["items"]> = { data: list.data?.items, error: list.error };
  // /api/ipa/detail, /api/ipa ve dashboard bitmeden çağrılmaz: belge KPI 3'ün bulduğu belgedir
  const ipaNo = d.data && nearestDoc(d.data, ipa)?.ipaNo;
  const [detail, setDetail] = useState<Part<IpaDetailResponse>>({});
  useEffect(() => { if (ipaNo) getIpaDetail(ipaNo).then((data) => setDetail({ data })).catch((e) => setDetail({ error: errorText(e) })); }, [ipaNo]);
  return <DashboardView d={d} run={run} ipa={ipa} ipaDetail={detail} sources={sources} integrity={integrity} />;
}
